import { useCallback, useEffect, useState } from "react";
import { BellOff, Loader2, Pencil, Plus, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatDay } from "../../lib/finance";
import { PREF_STATE_LABELS, PREF_STATE_TONES, emptyPrefForm, marketingError, prefPayload, prefProblem, touchKindLabel, touchPayload, touchProblem } from "../../lib/marketing";
import { StateBadge } from "./MarketingBits";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../ui/select";

function TouchDialog({ prospectId, onClose, onDone }) {
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState("");
  const [on, setOn] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/marketing/origin-options").then(({ data }) => { if (alive) setOptions(data); }).catch(() => { if (alive) setOptions({ campaigns: [], partners: [], channels: [] }); });
    return () => { alive = false; };
  }, []);
  const problem = touchProblem(choice, on);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/prospects/${prospectId}/origin/touches`, touchPayload(choice, on, note));
      toast.success("Origine enregistrée");
      onDone(data);
    } catch (e) {
      setError(marketingError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="touch-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une origine</DialogTitle>
          <DialogDescription>Par où cette pharmacie est-elle arrivée ? L'origine la plus ancienne compte pour les chiffres ; les autres restent visibles.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Origine</Label>
            <Select value={choice || undefined} onValueChange={setChoice}>
              <SelectTrigger className="h-10" aria-label="Origine" data-testid="touch-choice"><SelectValue placeholder={options ? "Choisir une origine" : "Chargement…"} /></SelectTrigger>
              <SelectContent>
                {options?.campaigns.length > 0 && <SelectGroup><SelectLabel>Campagnes</SelectLabel>{options.campaigns.map((c) => <SelectItem key={c.id} value={`campaign:${c.id}`}>{c.name}</SelectItem>)}</SelectGroup>}
                {options?.partners.length > 0 && <SelectGroup><SelectLabel>Partenaires</SelectLabel>{options.partners.map((p) => <SelectItem key={p.id} value={`partner:${p.id}`}>{p.name}</SelectItem>)}</SelectGroup>}
                {options?.channels.length > 0 && <SelectGroup><SelectLabel>Canal seul</SelectLabel>{options.channels.map((c) => <SelectItem key={c.code} value={`channel:${c.code}`}>{c.label}</SelectItem>)}</SelectGroup>}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label htmlFor="touch-on">Date du contact (aujourd'hui par défaut)</Label>
            <Input id="touch-on" type="date" value={on} onChange={(e) => setOn(e.target.value)} className="h-10" data-testid="touch-on" /></div>
          <div className="space-y-1.5"><Label htmlFor="touch-note">Note (facultative)</Label>
            <Input id="touch-note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} className="h-10" data-testid="touch-note" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="touch-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="touch-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ prospectId, touch, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/prospects/${prospectId}/origin/touches/${touch.id}/cancel`, { reason: reason.trim() });
      toast.success("Origine annulée");
      onDone(data);
    } catch (e) {
      setError(marketingError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="touch-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Annuler l'origine « {touch.origin.name} » ?</DialogTitle>
          <DialogDescription>L'origine reste visible, barrée, avec son motif : rien n'est supprimé. Elle ne compte plus dans les chiffres.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5"><Label htmlFor="touch-reason">Motif</Label>
          <Textarea id="touch-reason" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="touch-reason" /></div>
        {error && <p className="text-sm text-red-600" role="alert" data-testid="touch-cancel-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="touch-cancel-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Annuler l'origine</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreferencesDialog({ prospectId, origin, onClose, onDone }) {
  const prefs = origin.preferences;
  const [form, setForm] = useState(() => emptyPrefForm(prefs));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const problem = prefProblem(form);
  const setChannel = (code, patch) => setForm((f) => ({ ...f, channels: { ...f.channels, [code]: { ...f.channels[code], ...patch } } }));
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put(`/prospects/${prospectId}/preferences`, prefPayload(form, prefs.version));
      toast.success("Préférences enregistrées");
      onDone(data);
    } catch (e) {
      setError(e?.response?.status === 409 ? "Ces préférences ont changé entre-temps. Fermez et rouvrez pour voir la dernière version." : marketingError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="prefs-dialog">
        <DialogHeader>
          <DialogTitle>Préférences de communication</DialogTitle>
          <DialogDescription>Vous enregistrez ce que la pharmacie a déclaré ; Jamora ne juge pas de sa validité. Tant qu'un canal est « inconnu », il n'est pas ciblé.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {origin.contact_channels.map(({ code, label }) => {
            const c = form.channels[code];
            return (
              <div key={code} className="space-y-2 rounded-md border border-slate-200 p-3" data-testid={`pref-${code}`}>
                <div className="flex items-center justify-between gap-2">
                  <Label>{label}</Label>
                  <Select value={c.state} onValueChange={(state) => setChannel(code, { state })}>
                    <SelectTrigger className="h-9 w-36" aria-label={`État ${label}`} data-testid={`pref-${code}-state`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PREF_STATE_LABELS).map(([id, text]) => <SelectItem key={id} value={id}>{text}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {c.state !== "unknown" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1"><Label className="text-xs">Date</Label>
                      <Input type="date" value={c.on} onChange={(e) => setChannel(code, { on: e.target.value })} className="h-9" data-testid={`pref-${code}-on`} /></div>
                    {c.state === "allowed" && (
                      <div className="space-y-1"><Label className="text-xs">Base déclarée</Label>
                        <Select value={c.basis || undefined} onValueChange={(basis) => setChannel(code, { basis })}>
                          <SelectTrigger className="h-9" aria-label="Base déclarée" data-testid={`pref-${code}-basis`}><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent>{origin.legal_bases.map((b) => <SelectItem key={b.code} value={b.code}>{b.label}</SelectItem>)}</SelectContent>
                        </Select></div>
                    )}
                    <div className="space-y-1 sm:col-span-2"><Label className="text-xs">D'où vient la preuve</Label>
                      <Input value={c.evidence} maxLength={300} placeholder="Formulaire du salon, échange noté par…" onChange={(e) => setChannel(code, { evidence: e.target.value })} className="h-9" data-testid={`pref-${code}-evidence`} /></div>
                  </div>
                )}
              </div>
            );
          })}
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={form.do_not_contact} onCheckedChange={(v) => setForm((f) => ({ ...f, do_not_contact: !!v }))} className="mt-0.5" data-testid="pref-dnc" />
            <span><span className="font-medium text-slate-900">Ne plus solliciter</span><br /><span className="text-slate-500">Prioritaire sur tous les canaux ci-dessus.</span></span>
          </label>
          {form.do_not_contact && (
            <div className="space-y-1"><Label htmlFor="pref-dnc-reason" className="text-xs">Motif</Label>
              <Input id="pref-dnc-reason" value={form.do_not_contact_reason} maxLength={300} onChange={(e) => setForm((f) => ({ ...f, do_not_contact_reason: e.target.value }))} className="h-9" data-testid="pref-dnc-reason" /></div>
          )}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="prefs-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="prefs-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// « Origine et préférences » on the prospect sheet: where the pharmacy came from (append-only) and what it accepts to receive. The
// « do not contact » flag is a protection, shown to every reader of the sheet.
export default function ProspectOrigin({ prospectId }) {
  const [origin, setOrigin] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data } = await api.get(`/prospects/${prospectId}/origin`);
      setOrigin(data);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [prospectId]);
  useEffect(() => { load(); }, [load]);
  const done = (data) => { setOrigin(data); setDialog(null); };

  if (phase === "loading") return <Card className="p-4 space-y-2"><Skeleton className="h-5 w-48" /><Skeleton className="h-16 w-full" /></Card>;
  if (phase === "error") {
    return (
      <Card className="p-4 text-sm text-slate-600" data-testid="origin-error">
        Impossible de charger l'origine de cette pharmacie. <Button variant="link" className="p-0 h-auto" onClick={load}><RefreshCw size={12} className="mr-1" />Réessayer</Button>
      </Card>
    );
  }
  const prefs = origin.preferences;
  const knownChannels = origin.contact_channels.filter(({ code }) => prefs.channels[code].state !== "unknown");
  return (
    <Card className="p-4 space-y-4" data-testid="prospect-origin">
      {prefs.do_not_contact && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status" data-testid="origin-dnc">
          <BellOff size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <div><span className="font-medium">Ne plus solliciter.</span> Cette pharmacie a demandé à ne plus recevoir de sollicitation commerciale.{prefs.do_not_contact_reason ? ` ${prefs.do_not_contact_reason}` : ""}</div>
        </div>
      )}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="font-display font-semibold text-slate-900">Origine</h3>
          {origin.can_record && <Button variant="outline" size="sm" onClick={() => setDialog("touch")} data-testid="touch-add"><Plus size={14} className="mr-1.5" />Ajouter</Button>}
        </div>
        {origin.touches.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="origin-empty">Aucune origine enregistrée.{origin.can_record ? " Ajoutez la campagne, le partenaire ou le canal par lequel elle est arrivée." : ""}</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="origin-touches">
            {origin.touches.map((t) => (
              <li key={t.id} className="flex items-start justify-between gap-3 py-2" data-testid={`touch-${t.id}`}>
                <div className={`min-w-0 ${t.cancelled ? "text-slate-400" : ""}`}>
                  <div className={`text-sm ${t.cancelled ? "line-through" : "text-slate-900 font-medium"}`}>
                    {t.origin.name} <span className="text-xs font-normal text-slate-500 no-underline">· {touchKindLabel(t.origin.kind)}</span>
                  </div>
                  <div className="text-xs text-slate-500">{formatDay(t.on)}{t.recorded_by ? ` · saisi par ${t.recorded_by}` : ""}{t.note ? ` · ${t.note}` : ""}</div>
                  {t.cancelled && <div className="text-xs text-slate-500">Annulée{t.cancelled.by ? ` par ${t.cancelled.by}` : ""}{t.cancelled.reason ? ` : ${t.cancelled.reason}` : ""}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.attributed && <Badge variant="outline" className="text-2xs" data-testid="touch-attributed">Origine retenue</Badge>}
                  {origin.can_record && !t.cancelled && (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-600" onClick={() => setDialog({ cancel: t })} aria-label={`Annuler l'origine ${t.origin.name}`} data-testid={`touch-cancel-${t.id}`}>
                      <Undo2 size={14} />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="font-display font-semibold text-slate-900">Préférences de communication</h3>
          {origin.can_manage_consent && <Button variant="outline" size="sm" onClick={() => setDialog("prefs")} data-testid="prefs-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>}
        </div>
        {knownChannels.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="prefs-unknown">Aucune préférence enregistrée : cette pharmacie n'est pas ciblée par le marketing.</p>
        ) : (
          <ul className="space-y-1" data-testid="prefs-list">
            {knownChannels.map(({ code, label }) => (
              <li key={code} className="flex items-center gap-2 text-sm">
                <span className="w-24 text-slate-700">{label}</span>
                <StateBadge label={PREF_STATE_LABELS[prefs.channels[code].state]} tone={PREF_STATE_TONES[prefs.channels[code].state]} />
                {prefs.channels[code].on && <span className="text-xs text-slate-500">{formatDay(prefs.channels[code].on)}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
      {dialog === "touch" && <TouchDialog prospectId={prospectId} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.cancel && <CancelDialog prospectId={prospectId} touch={dialog.cancel} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "prefs" && <PreferencesDialog prospectId={prospectId} origin={origin} onClose={() => setDialog(null)} onDone={done} />}
    </Card>
  );
}
